/**
 * WaiverDocument — shared, presentational render of the approved waiver:
 * intro + numbered sections + per-section acknowledgment checkboxes + the
 * photo/media choice. Used by the public guest sign page (SignWaiver).
 *
 * The funnel's own waiver step (Book.js WaiverStep) renders the same document
 * inline; this component is the reusable version for standalone signing.
 *
 * Also exports two helpers used by signers:
 *   renderSignaturePng(name) — typed name → PNG data URL (stored signature)
 *   adultAge(ymd)            — whole years from a YYYY-MM-DD DOB (null if bad)
 */

import React from 'react';

export function renderSignaturePng(name) {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 160;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#0D2351';
  ctx.font = 'italic 52px "Brush Script MT", "Segoe Script", "Snell Roundhand", cursive';
  ctx.textBaseline = 'middle';
  ctx.fillText(name || '', 24, canvas.height / 2);
  return canvas.toDataURL('image/png');
}

export function adultAge(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd || '')) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const now = new Date();
  let age = now.getFullYear() - y;
  const md = now.getMonth() - (m - 1);
  if (md < 0 || (md === 0 && now.getDate() < d)) age -= 1;
  return age;
}

function Paragraphs({ lines }) {
  if (!lines) return null;
  return lines.map((t, i) => (
    <p
      key={i}
      style={{
        margin: '0 0 0.6rem',
        fontSize: '0.9rem',
        lineHeight: 1.5,
        color: 'rgba(0,0,0,0.8)',
      }}
    >
      {t}
    </p>
  ));
}

function Bullets({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ margin: '0 0 0.6rem', paddingLeft: '1.2rem' }}>
      {items.map((t, i) => (
        <li
          key={i}
          style={{
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: 'rgba(0,0,0,0.8)',
            marginBottom: '0.2rem',
          }}
        >
          {t}
        </li>
      ))}
    </ul>
  );
}

export default function WaiverDocument({
  waiver,
  acks,
  setAcks,
  photoChoice,
  setPhotoChoice,
}) {
  if (!waiver) return null;
  return (
    <div
      style={{
        maxHeight: '420px',
        overflowY: 'auto',
        padding: '1.25rem',
        background: '#FFFFFF',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '12px',
        marginBottom: '1.25rem',
      }}
    >
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: '#0D2351' }}>
        {waiver.operator}
      </h2>
      <p style={{ margin: '0 0 1rem', fontWeight: 700, color: '#0D2351' }}>
        {waiver.title}
      </p>
      <Paragraphs lines={waiver.intro} />

      {(waiver.sections || []).map((s) => (
        <div key={s.n} style={{ marginTop: '1.1rem' }}>
          <h3 style={{ margin: '0 0 0.4rem', fontSize: '0.95rem', color: '#0D2351' }}>
            {s.n}. {s.heading}
          </h3>
          <Paragraphs lines={s.body} />
          <Bullets items={s.bullets} />
          <Paragraphs lines={s.after} />

          {s.acknowledgment && !s.acknowledgment.minorOnly ? (
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
                padding: '0.6rem 0.75rem',
                background: acks[s.acknowledgment.id] ? '#FFFBEB' : '#F9FAFB',
                border: `1px solid ${
                  acks[s.acknowledgment.id] ? '#FCD34D' : 'rgba(0,0,0,0.12)'
                }`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.88rem',
                color: 'rgba(0,0,0,0.85)',
              }}
            >
              <input
                type="checkbox"
                checked={!!acks[s.acknowledgment.id]}
                data-testid={`waiver-ack-${s.acknowledgment.id}`}
                onChange={(e) =>
                  setAcks((prev) => ({
                    ...prev,
                    [s.acknowledgment.id]: e.target.checked,
                  }))
                }
                style={{ marginTop: '0.15rem', flexShrink: 0 }}
              />
              <span>{s.acknowledgment.label}</span>
            </label>
          ) : null}

          {s.choice ? (
            <div
              style={{
                padding: '0.6rem 0.75rem',
                background: '#F9FAFB',
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: '8px',
              }}
            >
              <p
                style={{
                  margin: '0 0 0.4rem',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: 'rgba(0,0,0,0.6)',
                }}
              >
                {s.choice.prompt}
              </p>
              {[
                { val: true, label: s.choice.agreeLabel },
                { val: false, label: s.choice.disagreeLabel },
              ].map((opt) => (
                <label
                  key={String(opt.val)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.6rem',
                    marginBottom: '0.3rem',
                    fontSize: '0.88rem',
                    color: 'rgba(0,0,0,0.85)',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name={s.choice.id}
                    checked={photoChoice === opt.val}
                    data-testid={`waiver-photo-${opt.val ? 'agree' : 'decline'}`}
                    onChange={() => setPhotoChoice(opt.val)}
                    style={{ marginTop: '0.15rem', flexShrink: 0 }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
